import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** Chiave dell'attributo carrello scritto all'accettazione dei termini Gift Card (chiave fissa, valore ISO 8601). */
const GIFT_CARD_CONSENT_ATTRIBUTE = 'Termini Gift Card accettati';

/**
 * Gestisce l'accettazione dei termini nel carrello (generali + gift card).
 * - Il pulsante checkout resta abilitato: al click senza spunta mostra l'errore e porta il focus sulla checkbox.
 * - I pulsanti di pagamento accelerato restano `inert` finché i termini richiesti non sono accettati.
 * - Dopo ogni aggiornamento del carrello (morph) lo stato viene riletto dal markup del server: la spunta va rimessa.
 */
class CartTermsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateState();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** Invocata da assets/morph.js dopo il re-render via Section Rendering API. */
  updatedCallback() {
    super.updatedCallback();
    this.#updateState();
  }

  /** Re-sync after morphSection (which runs synchronously before microtasks). */
  #handleCartUpdate = () => {
    queueMicrotask(() => this.#updateState());
  };

  /** Called by on:change="/onTermsChange" on the general terms checkbox. */
  onTermsChange() {
    const checkbox = this.querySelector('#accept-terms');
    if (checkbox instanceof HTMLInputElement && checkbox.checked) {
      this.#hideError('terms-error');
    }
    this.#updateState();
  }

  /** Called by on:change="/onGiftCardTermsChange" on the gift card terms checkbox. */
  onGiftCardTermsChange() {
    const checkbox = this.querySelector('#accept-gift-card-terms');
    if (checkbox instanceof HTMLInputElement && checkbox.checked) {
      this.#hideError('gift-card-terms-error');
      this.#saveGiftCardConsent();
    }
    this.#updateState();
  }

  /**
   * Called by on:click="/onCheckoutClick" on the checkout button.
   * @param {Event} event
   */
  onCheckoutClick(event) {
    if (this.#allTermsAccepted()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    let focused = false;

    const termsCheckbox = this.querySelector('#accept-terms');
    if (termsCheckbox instanceof HTMLInputElement && !termsCheckbox.checked) {
      this.#showError('terms-error');
      if (!focused) {
        termsCheckbox.focus();
        focused = true;
      }
    }

    const giftCardCheckbox = this.querySelector('#accept-gift-card-terms');
    if (giftCardCheckbox instanceof HTMLInputElement && !giftCardCheckbox.checked) {
      this.#showError('gift-card-terms-error');
      if (!focused) {
        giftCardCheckbox.focus();
      }
    }
  }

  #allTermsAccepted() {
    const termsCheckbox = this.querySelector('#accept-terms');
    const giftCardCheckbox = this.querySelector('#accept-gift-card-terms');
    return (
      (!(termsCheckbox instanceof HTMLInputElement) || termsCheckbox.checked) &&
      (!(giftCardCheckbox instanceof HTMLInputElement) || giftCardCheckbox.checked)
    );
  }

  /** Sblocca i pagamenti accelerati solo quando tutti i termini richiesti sono accettati. */
  #updateState() {
    const allOk = this.#allTermsAccepted();
    const accelerated = this.querySelector('.additional-checkout-buttons');

    if (accelerated instanceof HTMLElement) accelerated.toggleAttribute('inert', !allOk);

    this.toggleAttribute('terms-accepted', allOk);
  }

  #showError(id) {
    const el = this.querySelector(`#${id}`);
    if (el instanceof HTMLElement) el.hidden = false;
  }

  #hideError(id) {
    const el = this.querySelector(`#${id}`);
    if (el instanceof HTMLElement) el.hidden = true;
  }

  /** Scrive negli attributi del carrello la data di accettazione dei termini Gift Card (la nota del cliente non viene toccata). */
  #saveGiftCardConsent() {
    fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [GIFT_CARD_CONSENT_ATTRIBUTE]: new Date().toISOString() } }),
    }).catch(console.error);
  }
}

if (!customElements.get('cart-terms-component')) {
  customElements.define('cart-terms-component', CartTermsComponent);
}
